<script lang="ts">
	//UI
	import { Card } from "@hitit/new_ui";
	import ComponentCover from "@hitit/hub/src/v7/components/common/ComponentCover.svelte";
	import ComponentLoading from "@hitit/hub/src/v7/components/common/ComponentLoading.svelte";
	import EditSave from "../Common/EditSave.svelte";
	import StoreInfomation from "./DetailViewer/StoreInfomation.svelte";
	import KoreanRegistration from "./DetailViewer/KoreanRegistration.svelte";
	//Utils
	import {
		additionalInfoKey,
		approvalRequired,
		approvalRequiredLabels,
	} from "./DetailViewer/AdditionalInfo";
	import {
		api,
		notifications,
		t,
		store as storeStore,
	} from "@hitit/hub/src/stores";
	//Types
	import type { AddressData, StoreData } from "@hitit/api";
	import { getT } from "@hitit/utils";
	import type { CustomError } from "@hitit/hub/src/utils/types";
	//Props
	export let coverStatus = false;
	export let editmode = false;
	export let store: StoreData;

	//Constants
	const storeApprovalReason = "";
	const expectedAPIErrorTranslations = [
		{ name: "STORE_STATUS_NOT_UPDATED", message: "상태변경에 실패하였습니다." },
		{
			name: "STORE_INPUT_PLEASE_ENTER_ALL",
			message: "빈칸을 전부 채우십시오.",
		},
		{
			name: "Request failed with status code 403",
			message: "접근이 거부되었습니다. 접근 가능한 계정으로 시도해주세요.",
		},
	];
	//Variables
	let loading = false;
	let additionalInfoObject: Record<string, string> = {};
	let address: AddressData = {
		lat: 0,
		lng: 0,
		formattedAddressEng: null,
		formattedAddress1: "",
		formattedAddress2: "",
		detailAddress: "",
		country: "",
		source: "",
	};
	let storeMainInfo = {
		phonenumber: "",
		phonenumberCountryCode: "",
		type1: "",
		type2: "",
		appLockEnabled: false,
		storeDescription: "",
		storename: "",
		storeStatus: "",
		storeImageURL: "",
	};

	//Functions
	async function editHandler() {
		if (!editmode) {
			editmode = true;
			return;
		}
		try {
			loading = true;
			//
			const approvedStatus = storeMainInfo.storeStatus === "APPROVED";
			const originStoreId = store.id;
			const oldTranslationDTO = store.translationDTOs.find(
				(v) => v.lang === "ko" || v.defaultLang
			);

			const oldTranslationDTOIndex = store.translationDTOs.findIndex(
				(v) => oldTranslationDTO === v
			);
			const newAddressDTO = Object.assign({}, store.addressDTO, { ...address });
			const newTranslationDTO = Object.assign({}, oldTranslationDTO || {}, {
				name: storeMainInfo.storename,
				description: storeMainInfo.storeDescription,
				id: "",
				lang: oldTranslationDTO ? oldTranslationDTO.lang : "ko",
				defaultLang: oldTranslationDTO ? oldTranslationDTO.defaultLang : true,
			});

			if (approvedStatus)
				additionalInfoObject["KO_REGISTER_FOR_CASH_RECEIPT"] = "true";
			const newAdditionalInfo = Object.entries(additionalInfoObject)
				.map(([key, value]) => ({ key, value }))
				.filter((v) => v.value !== "")
				.map((item) => {
					// store.storeAdditionalInfoDTOList에서 같은 key를 가진 항목을 찾음
					const matched = store.storeAdditionalInfoDTOList.find(
						(dto) => dto.key === item.key
					);

					return matched ? { ...item, id: matched.id } : { ...item };
				});

			const verifyResult = verifyApproval(newAdditionalInfo);
			if (!verifyResult.success) {
				const error = new Error("STORE_INPUT_PLEASE_ENTER_ALL");
				error.name = "STORE_INPUT_PLEASE_ENTER_ALL";
				// 누락된 키 목록을 error 객체에 담기
				(error as any).missingKeys = verifyResult.missingKeys;
				(error as any).missingLabels = verifyResult.missingLabels;
				throw error;
			}

			// 승인 완료가 아니더라도 필수값이 들어가지 않으면 서버에서 wrong_format날림
			// if (!verifyApproval(newAdditionalInfo)) {
			// 	const error = new Error("STORE_INPUT_PLEASE_ENTER_ALL");
			// 	error.name = "STORE_INPUT_PLEASE_ENTER_ALL";
			// 	throw error;
			// }

			store.storeApplicationStatus = storeMainInfo.storeStatus;
			store.phonenumber = storeMainInfo.phonenumber;
			store.phonenumberCountryCode = storeMainInfo.phonenumberCountryCode;
			store.addressDTO = newAddressDTO;
			store.storeAdditionalInfoDTOList = newAdditionalInfo;
			store.appLockEnabled = storeMainInfo.appLockEnabled;
			if (oldTranslationDTOIndex >= 0) {
				store.translationDTOs[oldTranslationDTOIndex] = newTranslationDTO;
			} else {
				store.translationDTOs.push(newTranslationDTO);
			}

			// Update Store
			await $api.updateStore(store);

			// Update Store Application Status
			const storeStatusResponse = await $api.updateStoreApplicationStatus(
				store.id,
				store.storeApplicationStatus,
				storeApprovalReason
			);
			const storeStatusUpdated = storeStatusResponse.data.status === "OK";

			if (!storeStatusUpdated) {
				const error = new Error("STORE_STATUS_NOT_UPDATED");
				error.name = "STORE_STATUS_NOT_UPDATED";
				throw error;
			}

			if (originStoreId === store.id) {
				const newStore = (storeStatusResponse.data.result as StoreData[]).find(
					(v) => v.id === originStoreId
				) as StoreData;
				editmode = false;
				store = newStore;
				$storeStore = newStore;
				notifications.append(
					"SUCCESS",
					`${storeMainInfo.storeStatus} 변경완료`,
					"",
					undefined,
					"SUCCESS",
					3000
				);
			}
		} catch (_error) {
			const error = _error as CustomError & {
				missingKeys?: string[];
				missingLabels?: string[];
			};
			const name = error.message;
			const errorMessage = error.response?.data.error;

			const translation = expectedAPIErrorTranslations.find(
				(v) => v.name === name
			);
			let message = translation ? translation.message : errorMessage;

			// 누락된 항목이 있으면 추가로 표시
			if (error.missingLabels && error.missingLabels.length > 0) {
				message += `<br><br>누락된 항목:<br>${error.missingLabels.join(
					"<br>"
				)}`;
			}

			notifications.append("ERROR", message, "", undefined, "WARNING");
		} finally {
			loading = false;
		}
	}

	function dataMapping(store: StoreData) {
		// Mapping additional info
		additionalInfoObject = {};
		for (const key of additionalInfoKey) {
			additionalInfoObject[key] = "";
		}

		for (const data of store.storeAdditionalInfoDTOList) {
			const key = data.key;
			additionalInfoObject[key] = data.value;
		}

		// Address
		store.addressDTO.detailAddress = store.addressDTO.detailAddress ?? "";
		address = { ...store.addressDTO };
		// MainInfo
		storeMainInfo.appLockEnabled = store.appLockEnabled || false;
		storeMainInfo.phonenumber = store.phonenumber || "";
		storeMainInfo.phonenumberCountryCode = store.phonenumberCountryCode || "82";
		storeMainInfo.type1 =
			(store.storeTypeCodeDTO &&
				getT(store.storeTypeCodeDTO.translationDTOs, "type1", "ko")) ||
			"";

		storeMainInfo.type2 =
			(store.storeTypeCodeDTO &&
				getT(store.storeTypeCodeDTO.translationDTOs, "type2", "ko")) ||
			"";
		storeMainInfo.storename = getT(store.translationDTOs, "name", "ko") || "";
		storeMainInfo.storeDescription =
			getT(store.translationDTOs, "description", "ko") || "";
		storeMainInfo.storeStatus = store.storeApplicationStatus;
	}

	function verifyApproval(
		storeAdditionalInfoList: { key: string; value: string }[]
	): { success: boolean; missingKeys: string[]; missingLabels: string[] } {
		const missingKeys: string[] = [];
		const missingLabels: string[] = [];

		for (const require of approvalRequired) {
			const find = storeAdditionalInfoList.find((v) => v.key === require);
			if (!(find && find.value !== "")) {
				missingKeys.push(require);
				missingLabels.push(approvalRequiredLabels[require] || require);
			}
		}

		return {
			success: missingKeys.length === 0,
			missingKeys,
			missingLabels,
		};
	}

	//Do
	$: dataMapping(store);
</script>

<Card noPadding>
	<div class="container" class:editmode={editmode} t-id={"detailviewer"}>
		{#if coverStatus}
			<ComponentCover />
		{/if}
		{#if loading}
			<ComponentLoading coverMode />
		{/if}
		<EditSave actionHandler={editHandler} editmode={editmode} />
		<StoreInfomation
			type1={storeMainInfo.type1}
			type2={storeMainInfo.type2}
			bind:storeImageURL={storeMainInfo.storeImageURL}
			bind:formattedAddress1={address.formattedAddress1}
			bind:formattedAddress2={address.formattedAddress2}
			bind:lat={address.lat}
			bind:lng={address.lng}
			bind:detailAddress={address.detailAddress}
			bind:appLockEnabled={storeMainInfo.appLockEnabled}
			bind:phonenumber={storeMainInfo.phonenumber}
			bind:phonenumberCountryCode={storeMainInfo.phonenumberCountryCode}
			bind:storeDescription={storeMainInfo.storeDescription}
			bind:storename={storeMainInfo.storename}
			bind:storeStatus={storeMainInfo.storeStatus}
			bind:smartpayAvailable={additionalInfoObject[
				"KO_IS_MOBILE_PAYMENT_AVAILABLE"
			]}
			cacheReceiptAvailable={additionalInfoObject[
				"KO_IS_CASH_RECEIPT_AVAILABLE"
			]}
			editmode={editmode}
		/>
		<div class="seperate-bar" />
		<KoreanRegistration
			editmode={editmode}
			bind:ownername={additionalInfoObject["KO_OWNER_NAME"]}
			bind:membership_status={additionalInfoObject[
				"KO_IS_CASH_RECEIPT_AVAILABLE"
			]}
			bind:linkedMemberShip={additionalInfoObject[
				"KO_REGISTRATION_LINKED_CASHRECEIPT_MEMBERSHIP"
			]}
			bind:registrationNumber={additionalInfoObject["KO_REGISTRATION_NUMBER"]}
			bind:registrationImage={additionalInfoObject["KO_REGISTRATION_FILE_ADDR"]}
			bind:storeType={additionalInfoObject["KO_REGISTRATION_STORETYPE"]}
			bind:storeName={additionalInfoObject["KO_REGISTRATION_STORENAME"]}
			bind:storeClass={additionalInfoObject["KO_REGISTRATION_STORECLASS"]}
			bind:storeAddr={additionalInfoObject["KO_REGISTRATION_STORE_ADDR"]}
			bind:contactName={additionalInfoObject["KO_REGISTRATION_CONTACTNAME"]}
			bind:contactEmail={additionalInfoObject["KO_REGISTRATION_CONTACTEMAIL"]}
			bind:contactPhone={additionalInfoObject["KO_REGISTRATION_CONTACTNUMBER"]}
		/>
	</div>
</Card>

<style lang="scss">
	@import "../../../variables.scss";
	.container {
		display: flex;
		flex-direction: column;
		padding: 34px 10px 20px 10px;
		position: relative;
		width: 100%;
		gap: 20px;

		&.editmode {
			padding: 44px 10px 20px 10px;
		}
		.seperate-bar {
			width: 100%;
			height: 1px;
			background-color: #d3d4da;
		}
	}

	@include DesktopSize {
		.container {
			gap: 30px;
			padding: 30px 30px 40px 30px;

			&.editmode {
				padding: 30px 30px 40px 30px;
			}
		}
	}
</style>
